/**
 * Curses on the wire: the loadout a player picks, and the copy for the four
 * effects.
 *
 * `docs/DESIGN.md`, "Versus": "Players pick three before the match. Effects
 * are server-issued." `docs/prompt-review.md` R7: "players pick a *loadout* of
 * three before the match; the server decides when effects fire and is the only
 * issuer during play." A client that picked its own curses would be a client
 * that ignores them — every effect that touches a board arrives as a
 * `CurseWindow`, named by the ticks it starts and ends on, exactly the way a
 * `GarbageSchedule` is: both ends apply it, neither invents it.
 *
 * What an effect *does* to a board, and the window type itself, are
 * simulation rules and live in `@interlock/sim`. The durations and the
 * cooldown are in `limits.ts`, next to the rest of the netcode's numbers.
 */
import { isCurseEffect } from '@interlock/sim';
import type { CurseEffect } from '@interlock/sim';

/**
 * A loadout: exactly three of the four effects, chosen once before the match.
 * `isCurseLoadout` is the one place "three, distinct, from the four" is
 * checked — a message that fails it is treated exactly like one that never
 * arrived (see `messages.ts#parseLoadoutMessage`).
 */
export type CurseLoadout = readonly CurseEffect[];

/**
 * A picked-but-never-sent player still has to be able to play. `mirror` is
 * left out rather than `blind` — of the two disorienting effects (mirror,
 * rush) and the two obscuring ones (fog, blind), dropping one from each pair
 * would leave a loadout with no representative of a kind; dropping `mirror`
 * alone keeps three of the four `docs/DESIGN.md` effects live with nothing
 * missing in *kind*, which is the least arbitrary single choice available.
 */
export const DEFAULT_CURSE_LOADOUT: CurseLoadout = ['fog', 'rush', 'blind'];

/** Exactly `size` of the four effects, each named once. */
export function isCurseLoadout(value: unknown, size: number): value is CurseLoadout {
  if (!Array.isArray(value) || value.length !== size) return false;
  if (!value.every(isCurseEffect)) return false;
  return new Set(value).size === value.length;
}

const CURSE_LABEL: Readonly<Record<CurseEffect, string>> = {
  fog: 'Fog',
  mirror: 'Mirror',
  rush: 'Rush',
  blind: 'Blind',
};

const CURSE_DETAIL: Readonly<Record<CurseEffect, string>> = {
  fog: 'Hides your stack. Your active piece and the well walls stay visible.',
  mirror: 'Swaps left and right for a few seconds.',
  rush: 'Doubles your gravity for a few seconds.',
  blind: 'Hides your next queue. Hold stays visible.',
};

export function describeCurseEffect(effect: CurseEffect): string {
  return CURSE_LABEL[effect];
}

export function describeCurseEffectDetail(effect: CurseEffect): string {
  return CURSE_DETAIL[effect];
}
